/**
 * @author        matt@zumper.com (Matthieu Devlin)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   utility methods for traktor
 */

import { IS_BROWSER, isZumper } from '@zcode/env'
import {
  type OptimizelyClient,
  getVisitedExperiments,
} from '@zcode/optimizely-client'
import type { MaybeEmpty } from '@zcode/utility-types'
import { generateUid, getUserAgent, toSnakeCase } from '@zcode/utils'
import { format, getUnixTime } from 'date-fns'
import { logger } from '../../logger'
import type { Location, User } from '../actions'
import {
  LOCAL_FAVORITES_SYNC,
  PADMAPPER,
  SESSION,
  TRAKTOR_API_GATEWAY_URL,
  ZUMPER,
} from '../constants'
import { getBrowserInfo } from './getBrowserInfo'
import { type LocalStorage, getTrackingTokens } from './getTrackingTokens'

export interface InjectedHelpers<LS extends LocalStorage> {
  getGclidFromLocalStorage: (localStorage: LS) => string | undefined
  getMsclkidFromLocalStorage?: (localStorage: LS) => string | undefined
  localStorageSingleton: LS
}

interface FavoritesSyncEvent {
  readonly datetime: string
  readonly eventAttributes: {
    readonly localFavorites: unknown
  }
  readonly name: typeof LOCAL_FAVORITES_SYNC
  readonly sessionId: unknown
  readonly site: typeof PADMAPPER | typeof ZUMPER
  readonly uniqueId: unknown
  readonly userId: number | undefined
  readonly timestamp: number
}

type SessionEvent = ReturnType<typeof createSessionPayload>

type MiscEvent = ReturnType<typeof getCommonEventProperties> & {
  readonly name: string
}

type TraktorEvent = FavoritesSyncEvent | SessionEvent | MiscEvent

export const USER_ID_LOCAL_STORAGE_KEY = 'traktorUserId'

declare global {
  interface Window {
    readonly __GEO_LOCATION__: MaybeEmpty<{
      readonly cityName: string
      readonly country: string
      readonly lat: string
      readonly lng: string
      readonly stateName: string
    }>
    readonly __OPTIMIZELY__?: {
      readonly user: MaybeEmpty<{
        readonly attributes: {
          readonly isMobile: boolean
          readonly platform: string
          readonly query: string | null
        }
        readonly id: string
      }>
      readonly variationMap: Partial<Record<string, string>>
      readonly datafile: string
      readonly serverRenderedExperiments?: readonly string[]
    }
    traktorDeliverEvents?: typeof traktorDeliverEvents
    _traktorDeliverEvents?: typeof traktorDeliverEvents
  }
}

const SESSION_ID_LOCAL_STORAGE_KEY = 'traktorSessionId'
const SYNCED_FAVORITES_LOCAL_STORAGE_KEY = 'traktorLocalFavoritesSynced'
const LAST_TRACKED_SESSION_ID_LOCAL_STORAGE_KEY = 'traktorLastTrackedSessionId'

const THIRTY_MINUTES_IN_DAYS = 1 / 24 / 2
const BATCH_TIME = 1000 // 1 sec in milliseconds
const VERSION = '3.11.0'

let pendingEvents: TraktorEvent[] = []
let idleCallback: number | undefined

let sentSessionEvent = false
let optimizelyClient: OptimizelyClient | null = null

/**
 * sets optimizely client reference for traktor
 */
export const setOptimizelyClient = (optimizely: OptimizelyClient) => {
  optimizelyClient = optimizely
}

/**
 * the uniqueId
 */
export const getTraktorUserId = (localStorageSingleton: LocalStorage) => {
  let traktorUserId = localStorageSingleton.get(USER_ID_LOCAL_STORAGE_KEY)
  if (!traktorUserId) {
    traktorUserId = generateUid()
    localStorageSingleton.set(USER_ID_LOCAL_STORAGE_KEY, traktorUserId)
  }
  return traktorUserId
}

/**
 * Session id will be valid for as long as the user emits an event in the previous 30 mins
 */
const getTraktorSessionId = (localStorageSingleton: LocalStorage) => {
  let sessionId = localStorageSingleton.getx(SESSION_ID_LOCAL_STORAGE_KEY)
  if (!sessionId) {
    sessionId = generateUid()
  }
  // put back in the store with an updated expired time
  localStorageSingleton.setx(
    SESSION_ID_LOCAL_STORAGE_KEY,
    sessionId,
    THIRTY_MINUTES_IN_DAYS
  )
  return sessionId
}

/**
 * Requests an idle callback for sending events to the server
 *
 * @returns {number} callback ID
 */
const startRequestIdleCallback = () => {
  if (IS_BROWSER && typeof window !== 'undefined') {
    return window.requestIdleCallback(sendRequest, { timeout: BATCH_TIME })
  }
}

/**
 * default delivery implementation
 */
const traktorDeliverEvents = (events: readonly TraktorEvent[]) => {
  const handleErrors = (response: Response) => {
    if (!response.ok) {
      throw Error(response.statusText)
    }
    return response
  }
  return fetch(TRAKTOR_API_GATEWAY_URL, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'text/plain',
    },
    body: JSON.stringify(events),
  }).then(handleErrors)
}

// allow this to be overridden
let deliverEvents =
  (IS_BROWSER &&
    typeof window !== 'undefined' &&
    window.traktorDeliverEvents) ||
  traktorDeliverEvents
if (IS_BROWSER && typeof window !== 'undefined') {
  window.traktorDeliverEvents =
    window.traktorDeliverEvents || traktorDeliverEvents
  window._traktorDeliverEvents = traktorDeliverEvents
}

export const setDeliverEventsHandler = (
  deliver: typeof traktorDeliverEvents
) => {
  const prior = deliverEvents
  deliverEvents = deliver
  if (IS_BROWSER && typeof window !== 'undefined') {
    window.traktorDeliverEvents = deliver
  }
  return prior
}

/**
 * Sends queued events to server
 */
const sendRequest = () => {
  if (!pendingEvents || !pendingEvents.length) {
    return
  }
  const attemptedRequest = pendingEvents
  pendingEvents = []
  return deliverEvents(attemptedRequest)
    .catch((error: unknown) => {
      pendingEvents.push(...attemptedRequest)
      logger.debug('Error posting traktor events', error)
    })
    .then(() => (idleCallback = undefined))
}

/**
 * Adds an event to the queue to be sent to server
 *
 * @param payload event payload
 */
const queueEvent = (payload: TraktorEvent) => {
  if (IS_BROWSER) {
    const traktorPayload = {
      ...payload,
      versionNumber: VERSION,
    }
    pendingEvents.push(traktorPayload)
    idleCallback = idleCallback || startRequestIdleCallback()
  }
}

function createSessionPayload({
  datetime,
  email,
  localStorageSingleton,
  sessionId,
  site,
  timestamp,
  traktorUniqueId,
  userId,
}: {
  readonly datetime: string
  readonly email: string | undefined
  readonly localStorageSingleton: LocalStorage
  readonly sessionId: unknown
  readonly site: typeof PADMAPPER | typeof ZUMPER
  readonly traktorUniqueId: unknown
  readonly timestamp: number
  readonly userId: number | undefined
}) {
  const optimizelyAudience = optimizelyClient?.user?.attributes
  const userAgent = getUserAgent()
  const { referrer } = document
  const browserAgent: string = optimizelyAudience?.browser || userAgent
  const { name: browser, version: browserVersion } =
    getBrowserInfo(browserAgent)
  const isMobile: boolean | undefined =
    // @ts-expect-error __PRELOADED_STATE__ "doesn't exist"
    typeof window.__PRELOADED_STATE__ === 'string'
      ? // @ts-expect-error __PRELOADED_STATE__ "doesn't exist"
        JSON.parse(window.__PRELOADED_STATE__).app.mobile // web-pro and web-renter
      : // @ts-expect-error __PRELOADED_STATE__ "doesn't exist"
        window.__PRELOADED_STATE__?.app?.mobile

  return {
    name: SESSION,
    sessionId,
    uniqueId: traktorUniqueId,
    userId,
    email,
    site,
    referrer: redactUrl(referrer),
    referrerDomain: referringDomain(referrer),
    userAgent,
    timestamp,
    datetime,
    browser,
    browserVersion,
    os: optimizelyAudience?.os || os(userAgent),
    screenHeight: window.screen.height,
    screenWidth: window.screen.width,
    abTests: getVisitedExperiments(optimizelyClient),
    allAbTests: {},
    optimizelyUserId:
      optimizelyClient?.user?.id ?? window.__OPTIMIZELY__?.user?.id,
    trackingTokens: getTrackingTokens(localStorageSingleton),
    platform:
      optimizelyAudience?.platform ??
      [
        typeof isMobile === 'boolean'
          ? isMobile
            ? 'smartphone'
            : 'desktop'
          : undefined,
        'web',
      ]
        .filter(Boolean)
        .join(' '),
    cityState: window.__GEO_LOCATION__.cityName
      ? toSnakeCase(
          [window.__GEO_LOCATION__.cityName, window.__GEO_LOCATION__.stateName]
            .filter(Boolean)
            .join(' ')
        ).replace(/_/g, '-')
      : undefined,
  } as const
}

/**
 * Adds a session event to the queue and an event for local storage sync if necessary
 */
const trackSession = (
  user: User | undefined,
  localStorageSingleton: LocalStorage
) => {
  const sessionId = getTraktorSessionId(localStorageSingleton)
  const nowUtc = new Date().valueOf()
  const traktorUniqueId = getTraktorUserId(localStorageSingleton)
  const site = isZumper ? ZUMPER : PADMAPPER
  const timestamp = nowUtc.valueOf()
  const datetime = format(nowUtc, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
  const { userId, email } = user || {}

  if (
    localStorageSingleton.get(LAST_TRACKED_SESSION_ID_LOCAL_STORAGE_KEY) !==
    sessionId
  ) {
    queueEvent(
      createSessionPayload({
        datetime,
        email,
        localStorageSingleton,
        sessionId,
        site,
        timestamp,
        traktorUniqueId,
        userId,
      })
    )
    localStorageSingleton.set(
      LAST_TRACKED_SESSION_ID_LOCAL_STORAGE_KEY,
      sessionId
    )
  }

  const previouslySynced = localStorageSingleton.get(
    SYNCED_FAVORITES_LOCAL_STORAGE_KEY
  )
  if (!previouslySynced) {
    const localFavorites = localStorageSingleton.get('favorites')
    const favoritesPayload = {
      eventAttributes: {
        localFavorites,
      },
      name: LOCAL_FAVORITES_SYNC,
      sessionId,
      uniqueId: traktorUniqueId,
      userId,
      site,
      timestamp,
      datetime,
    } as const
    queueEvent(favoritesPayload)
    localStorageSingleton.set(
      SYNCED_FAVORITES_LOCAL_STORAGE_KEY,
      getUnixTime(new Date().valueOf())
    )
  }
}

/**
 * Tracks an event. Adds it to the queue to be sent if the bundle has been received (user
 * information is correct) or to the queue to be submitted after bundle received.
 *
 * @param name - event name
 * @param props - event props
 * @param filters - filters object as JS object (`Filters` from `@zcode/state-type`)
 * @param location - location object as JS object (`Location` from `@zcode/state-type`)
 */
export const trackEvent = <LS extends LocalStorage>(
  name: string,
  props: object | undefined,
  user: User | undefined,
  filters: object | undefined,
  location: Location | undefined,
  injectedHelpers: InjectedHelpers<LS>
) => {
  const commonEventProperties = getCommonEventProperties(
    user,
    filters,
    location,
    injectedHelpers
  )
  const payload = {
    ...commonEventProperties,
    eventAttributes: { ...commonEventProperties.eventAttributes, ...props },
    name,
  }

  if (!sentSessionEvent) {
    trackSession(user, injectedHelpers.localStorageSingleton)
    sentSessionEvent = true
  }
  queueEvent(payload)
}

/**
 * Gets the common events that are submitted in all events (except session event)
 *
 * @param filters - filters object as JS object (`Filters` from `@zcode/state-type`)
 * @param location - location object as JS object (`Location` from `@zcode/state-type`)
 */
const getCommonEventProperties = <LS extends LocalStorage>(
  user: User | undefined,
  filters: object | undefined,
  location: Location | undefined,
  {
    getGclidFromLocalStorage,
    getMsclkidFromLocalStorage,
    localStorageSingleton,
  }: InjectedHelpers<LS>
) => {
  const nowUtc = new Date().valueOf()
  const site = isZumper ? ZUMPER : PADMAPPER

  return {
    eventAttributes: {
      gclid: getGclidFromLocalStorage(localStorageSingleton),
      host: window.location.hostname,
      msclkid: getMsclkidFromLocalStorage?.(localStorageSingleton),
    },
    sessionId: getTraktorSessionId(localStorageSingleton),
    uniqueId: getTraktorUserId(localStorageSingleton),
    userId: user?.userId,
    viewportHeight: window.innerHeight,
    viewportWidth: window.innerWidth,
    url: redactUrl(window.location.href),
    site,
    timestamp: nowUtc.valueOf(),
    datetime: getUnixTime(nowUtc),
    abTests: getVisitedExperiments(optimizelyClient),
    allAbTests: {},
    optimizelyUserId:
      optimizelyClient?.user?.id ?? window.__OPTIMIZELY__?.user?.id,
    filters,
    location,
  }
}

/**
 * ZUM-53439: Redacts sensitive/PII values from a URL. This function is used for traktor.
 * For other analytics tools, this regex is duplicated in Tag Manager:
 * - In the "Rollbar Configure" tag for rollbar
 * - In the "Blueshift pageload" tag for Blueshift
 * - In the "Mixpanel Load" tag for Mixpanel
 * - In the "PII-Free Page URL" and "Google Analytics ID" variables for Google Analytics
 */
const redactUrl = (url: string) => {
  return url.replace(/([?&](?:email|token)=)[^&]+/g, '$1...')
}

const referringDomain = (referrer: string) => {
  const match = referrer.match(/https?:\/\/([^/]*)/)
  return match ? match[1] : ''
}

const os = (userAgent: string) => {
  if (/Windows/i.test(userAgent)) {
    if (/Phone/i.test(userAgent) || /WPDesktop/i.test(userAgent)) {
      return 'Windows Phone'
    }
    return 'Windows'
  }
  if (/(iPhone|iPad|iPod)/i.test(userAgent)) {
    return 'iOS'
  }
  if (/Android/i.test(userAgent)) {
    return 'Android'
  }
  if (/(BlackBerry|PlayBook|BB10)/i.test(userAgent)) {
    return 'BlackBerry'
  }
  if (/Mac/i.test(userAgent)) {
    return 'Mac OS X'
  }
  if (/Linux/i.test(userAgent)) {
    return 'Linux'
  }
  return ''
}
